/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React, { useEffect, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas/src';
import { Box, CircularProgress } from '@mui/material';
import { pdfjs } from 'react-pdf';
import PropTypes from 'prop-types';
import { useSignaturePadProvider } from '../../Provider/SignaturePadProvider';
import { useSignatureDrawPad } from './useSignatureDrawPad';

/**
 * SignatureDrawPad Component
 * @returns {Element}
 * @constructor
 */
export function SignatureDrawPad({ fileUri, workerScriptUri }) {
  // Set the worker src
  pdfjs.GlobalWorkerOptions.workerSrc = workerScriptUri;
  // Reference to ensure rendering process is only executed once
  const imageListRendered = useRef(false);
  // Provider properties
  const {
    imagesData, signaturePadsReference, color, navigationAction, drawPadChanges, setDrawPadChanges,
  } = useSignaturePadProvider();
    // Custom SignatureDrawPad hook
  const { renderImagesFromBase64PdfData, hex2rgba } = useSignatureDrawPad(fileUri);

  /**
     * Renders images from base64 PDF data when the component mounts or `imagesData` changes.
     *
     * This effect runs once when the component mounts and whenever `imagesData` or
     * `renderImagesFromBase64PdfData` change. It checks if the images have already been rendered.
     * If not, it sets a flag to prevent re-rendering and calls the `renderImagesFromBase64PdfData` function.
     *
     * @param {Object} imagesData - The data containing base64 encoded images.
     * @param {Function} renderImagesFromBase64PdfData - The function to render images from base64 PDF data.
     */
  useEffect(() => {
    if (!imageListRendered.current && !imagesData) {
      imageListRendered.current = true;
      renderImagesFromBase64PdfData();
    }
  }, [imagesData, renderImagesFromBase64PdfData]);

  /**
     * Renders signature pads or a loading indicator.
     *
     * This function returns a loading indicator if `imagesData` is not available.
     * Once `imagesData` is available, it maps through the data to render signature pads.
     * Each signature pad is configured based on the `navigationAction` and includes event
     * handlers for drawing changes.
     *
     * @returns {JSX.Element} The rendered signature pads or a loading indicator.
     */
  const renderSignaturePads = () => {
    if (!imagesData) {
      return (<CircularProgress />);
    }

    return (
      <>
        {imagesData.map((imageData, index) => {
          const signaturePad = signaturePadsReference.current[index];

          return (
          // eslint-disable-next-line react/no-array-index-key
            <Box sx={{ position: 'relative' }} key={`signature_pad_${index}`}>
              {navigationAction === 'touch' && <Box className="draw-pad-overlay" />}
              <SignatureCanvas
                throttle={0}
                minDistance={navigationAction === 'mark' ? 5 : 1}
                minWidth={navigationAction === 'mark' ? 20 : 0.5}
                ref={signaturePad ? undefined : (ref) => {
                  if (ref) {
                    // Set the reference
                    signaturePadsReference.current = [...signaturePadsReference.current, ref];
                    // Set the background image
                    ref.fromDataURL(imageData.image, { ratio: 1 });
                  }
                }}
                penColor={navigationAction === 'mark' ? hex2rgba(color, 0.04) : color}
                canvasProps={{ width: imageData.width, height: imageData.height, className: 'sigCanvas' }}
                onBegin={() => {
                  setDrawPadChanges([...drawPadChanges, {
                    index, image: signaturePadsReference.current[index].toDataURL(),
                  }]);
                }}
              />
            </Box>
          );
        })}
      </>
    );
  };

  return (
    <Box sx={{
      display: 'flex', justifyContent: 'center', margin: '32px 0', flexFlow: 'column', alignItems: 'center',
    }}
    >
      {renderSignaturePads()}
    </Box>

  );
}

SignatureDrawPad.propTypes = {
  fileUri: PropTypes.string.isRequired, workerScriptUri: PropTypes.string.isRequired,
};
